// 1:1 port from age-of-mcp v3 (src/client/models/v3/sign.ts) — the flattened
// part literals rebuilt from the original helpers + loops.
//
// v3 sign — A BIG READABLE NOTICE BOARD. Two oak posts carrying one wide
// pale canvas-faced board with dark "text" lines baked on (legible as
// writing even at minimap distance) under a little terracotta cap rail.
// Inscribed, so it never reorients to a road.

import { defineModel, styleV3, type Part } from "../../lib";

const { TIMBER, CANVAS, HAZARD, STONE_DK, ROOF_HOME, box, slab, poles } = styleV3;

const BASE = 0.04;            // a thin ground pad

function pad(): Part {
  return slab(0.22, 0.12, STONE_DK, BASE);
}

// The two oak posts, `h` tall.
function posts(h: number): Part[] {
  return [-0.14, 0.14].map((x) =>
    box(0.045, h, 0.045, TIMBER, [x, BASE + h / 2, 0]));
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [pad()];
  if (s === 1) return [pad(), ...posts(0.16), ...poles(0.3, 0.16, 0.2)];
  if (s === 2) return [pad(), ...posts(0.4), ...poles(0.3, 0.16, 0.3)];

  return [
    pad(),
    ...posts(0.4),
    // The wide written board spanning the posts.
    box(0.4, 0.22, 0.035, CANVAS, [0, 0.31, 0]),
    // "Text" lines on the face.
    box(0.3, 0.025, 0.012, HAZARD, [0, 0.36, 0.022]),
    box(0.24, 0.025, 0.012, HAZARD, [-0.03, 0.3, 0.022]),
    // A terracotta cap rail keeping the rain off.
    box(0.46, 0.035, 0.07, ROOF_HOME, [0, 0.445, 0]),
  ];
}

export const signV3 = defineModel({
  id: "sign-v3",
  tiles: { w: 1, h: 1 },
  batchId: "45-optimized",
  name: "Sign (v3)",
  tags: ["building","sign","low-poly","age-of-mcp","v3"],
  summary: "Low-poly sign.",
  description: "A wooden board carrying one short written message.",
  author: "age-of-mcp",
  footprint: 0.13,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
});
